import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useCallback, useState } from 'react';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { stripGreetingExchange } from '@hooks/features/bestieGreetingPrompt';
import { sanitizeHtml } from '@utils/strings/sanitizeHtml';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

export interface JobSearchFilters {
  keyword?: string;
  is_ambigous_keyword?: boolean;
  locations?: string[] | null;
  spoken_languages?: string[];
  remote?: boolean | null;
  abroad?: boolean | null;
  career_levels?: string[] | null;
  employment_types?: string[] | null;
  company_name?: string | null;
  min_salary?: number | null;
  activation_date?: string | null;
  domains?: number[] | null;
  benefits?: string[];
}

export interface RecommendationItem {
  slug: string;
  title: string;
  companyName: string;
  companyLogo: string;
  salary: string;
  locations: { name: string }[];
  score: number;
  url: string;
  type: string;
}

export interface CustomMessageData {
  customData?: {
    jobSearchResults?: {
      items: {
        id: number,
        score: number,
        data: ApiSchema<'JobItem'>
      }[],
      cursor: string,
      type: string,
      filters?: JobSearchFilters;
      search_keyword?: string;
    }
    jobRecommendationResults?: {
      items: RecommendationItem[];
    }
    jobSearchRequest?: Record<string, unknown>;
    resumeSearchRequest?: {
      keyword?: string,
      location?: string[],
      radiusInKm?: number,
      spokenLanguages?: string[],
      domains?: string[],
      driversLicense?: boolean,
      studyLevels?: string[],
      lastActivity?: number,
      careerLevels?: string[]
    }
    resumeSearchResults?: {
      items: ApiSchema<'ResumeCardData'>[],
      pages: number,
    }
  }
}

export interface Message {
  id: string;
  type: string;
  content: string;
  name?: string;
  keyword?: string;
  response_metadata?:CustomMessageData;
}

export interface Suggestions {
  data: string[];
  type: string;
  id: string;
  retry?: boolean;
}

export interface Filters {
  value: string;
  type: string;
}

export interface MessageMetadata {
  run_id?: string;
}

export interface ChatMessageFile {
  url: string;
  name?: string;
  type?: string;
}

export interface ChatMessage {
  role: 'human' | 'ai' | 'system' | 'tool' | 'unknown';
  content: string;
  files?: ChatMessageFile[] | null;
  images?: ChatMessageFile[] | null;
  structured_content?: Record<string, unknown> | unknown[] | string | null;
}

export interface ConversationHistoryResponse {
  user_id: string | null;
  last_message_at: string | null;
  messages: ChatMessage[];
  message_count: number;
}

export interface MessageHistoryResponse {
  messages: Message[];
  created_at: string;
  fetched?: boolean;
}

export interface StreamState {
  status: 'inflight' | 'error' | 'done';
  messages?: Message[];
  run_id?: string;
}

/** Optional backend-built prompt forwarded into the chat/stream body (resume interview). */
export interface StreamPrompt {
  parts: string[];
  variables?: Record<string, unknown>;
}

export interface StartStreamConfig {
  headers?: Record<string, string>;
  files?: File[];
  configurations?: Record<string, string>;
  /** Backend-built prompt — only sent for the resume-interview chat's first message. */
  prompt?: StreamPrompt;
  /** Backend-built extraction schema — only sent for the resume-interview chat. */
  extractionSchema?: unknown;
}

export interface StreamStateProps {
  stream: StreamState | null;
  filters: Filters | null;
  startStream: (
    input: Message,
    thread_id: string,
    config?: StartStreamConfig,
  ) => Promise<void>;
  stopStream?: (clear?: boolean) => void;
  initializeWithHistory: (
    callback?: () => void | Promise<void>,
  ) => Promise<MessageHistoryResponse>;
  isLoadingHistory: boolean;
  biaSuggestions?: Suggestions;
  clearBiaSuggestions: () => void;
  resetSearchFilters: () => void;
  /** Append a pre-built message to the stream (e.g. the agent's opening line from chat/invoke). */
  seedMessage: (message: Message) => void;
}

/**
 * Maps a message's `structured_content` tool payload into the `response_metadata` shape the
 * chat renderer reads (`ChatMessageComponent` renders job cards off `customData`).
 *
 * Shared by restored history (`mapChatMessagesToMessages`) and the chat/invoke opener, which
 * receives the same artifact object in its response. Returns `undefined` when the payload is
 * absent or of an unrecognised type, so callers can leave `response_metadata` unset.
 */
export const mapStructuredContentToMetadata = (
  structuredContent: ChatMessage['structured_content'],
): CustomMessageData | undefined => {
  if (!structuredContent || typeof structuredContent !== 'object' || Array.isArray(structuredContent)) {
    return undefined;
  }

  const structured = structuredContent;

  if (structured.type === 'job_search' && Array.isArray(structured.items)) {
    const jobStructured = structured as {
      type: string;
      items: { data: ApiSchema<'JobItem'> }[];
      filters?: JobSearchFilters;
      search_keyword?: string;
    };
    return {
      customData: {
        jobSearchResults: {
          items: (structured.items as { data: ApiSchema<'JobItem'> }[])
            .filter((item) => item?.data?.id)
            .map((item) => ({
              id: Number(item.data.id),
              score: 0,
              data: item.data,
            })),
          cursor: '',
          type: 'job_search',
          filters: jobStructured.filters,
          search_keyword: jobStructured.search_keyword,
        },
      },
    };
  }

  if (structured.type === 'job_recommendations' && Array.isArray(structured.items)) {
    return {
      customData: {
        jobRecommendationResults: {
          items: structured.items as RecommendationItem[],
        },
      },
    };
  }

  if (structured.type === 'resume_search' && structured.resume_search_result) {
    const result = structured.resume_search_result as { items: ApiSchema<'ResumeCardData'>[]; pages: number };
    return {
      customData: {
        resumeSearchResults: {
          items: result.items,
          pages: result.pages,
        },
      },
    };
  }

  return undefined;
};

/**
 * Maps ChatMessage objects from the API response to the internal Message format
 */
const mapChatMessagesToMessages = (chatMessages: ChatMessage[]): Message[] => chatMessages.map((msg, index) => {
  const type = msg.role === 'human' ? 'human' : msg.role;

  const message: Message = {
    id: `history-${index}`,
    type,
    content: msg.content,
  };

  const metadata = mapStructuredContentToMetadata(msg.structured_content);
  if (metadata) {
    message.response_metadata = metadata;
  }

  return message;
});

export function mergeMessagesById(
  left: Message[] | Record<string, Message[]> | null | undefined,
  right: Message[] | Record<string, Message[]> | null | undefined,
): Message[] {
  const leftMessages = Array.isArray(left) ? left : left?.messages;
  const rightMessages = Array.isArray(right) ? right : right?.messages;

  const merged = (leftMessages ?? [])?.slice();
  (rightMessages || []).forEach((msg) => {
    const foundIdx = merged.findIndex((m) => m.id === msg.id);
    if (foundIdx === -1) {
      merged.push(msg);
    } else {
      // Always preserve customData - use new one if available, otherwise keep existing
      const existingCustomData = merged[foundIdx]?.response_metadata?.customData;
      const newCustomData = msg?.response_metadata?.customData;
      const mergedCustomData = newCustomData || existingCustomData;

      merged[foundIdx] = {
        ...merged[foundIdx],
        ...msg,
        response_metadata: {
          ...merged[foundIdx]?.response_metadata,
          ...msg.response_metadata,
          ...(mergedCustomData ? { customData: mergedCustomData } : {}),
        },
      };
    }
  });

  return merged;
}

/**
 * Handles 'text' event - streaming text chunks from AI
 */
const handleTextEvent = (
  msg: { data: string },
  setCurrent: React.Dispatch<React.SetStateAction<StreamState | null>>,
) => {
  const chunk = JSON.parse(msg.data) as { content: string };

  setCurrent((prevCurrent) => {
    const existingMessages = prevCurrent?.messages || [];
    const lastMessage = existingMessages[existingMessages.length - 1];

    // Check if the last message is a streaming AI message we should append to
    if (lastMessage && lastMessage.type === 'ai' && lastMessage.id === 'streaming') {
      const updatedMessages = [...existingMessages];
      updatedMessages[updatedMessages.length - 1] = {
        ...lastMessage,
        content: lastMessage.content + chunk.content,
      };
      return {
        status: 'inflight',
        messages: updatedMessages,
        run_id: prevCurrent?.run_id,
      };
    }

    // Create a new streaming message
    const newMessage: Message = {
      id: 'streaming',
      type: 'ai',
      content: chunk.content,
    };

    return {
      status: 'inflight',
      messages: [...existingMessages, newMessage],
      run_id: prevCurrent?.run_id,
    };
  });
};

/**
 * Handles 'data' event - batch message updates
 */
const handleDataEvent = (
  msg: { data: string },
  setCurrent: React.Dispatch<React.SetStateAction<StreamState | null>>,
) => {
  const messages: Message[] | Record<string, Message[]> = JSON.parse(
    msg.data,
  ) as Message[] | { messages: Message[] };

  setCurrent((prevCurrent) => ({
    status: 'inflight',
    messages: mergeMessagesById(prevCurrent?.messages, messages),
    run_id: prevCurrent?.run_id,
  }));
};

/**
 * Handles 'resume_search_filter' event
 */
const handleResumeSearchFilterEvent = (
  msg: { data: string },
  setFilters: React.Dispatch<React.SetStateAction<Filters | null>>,
) => {
  setFilters({
    type: 'resume_search_filter',
    value: msg.data,
  });
};

/**
 * Handles 'metadata' event - run metadata updates
 */
const handleMetadataEvent = (
  msg: { data: string },
  setCurrent: React.Dispatch<React.SetStateAction<StreamState | null>>,
) => {
  const metadata: MessageMetadata = JSON.parse(
    msg.data,
  ) as MessageMetadata;

  setCurrent((prevCurrent) => ({
    status: 'inflight',
    messages: prevCurrent?.messages,
    run_id: metadata.run_id || prevCurrent?.run_id,
  }));
};

/**
 * Handles 'job_search_intent' event - redirects to job search page
 */
const handleJobSearchIntentEvent = (
  msg: { data: string },
  locale: string,
  router: ReturnType<typeof useExtendedRouter>['router'],
) => {
  const message = JSON.parse(msg.data) as Message;
  const { keyword } = message;
  const jobsPath = '/job/list';
  const path = keyword?.trim() ? `${jobsPath}/${keyword}` : jobsPath;
  const href = getI18nLinkHref(path, locale);
  void router.push(href, { withLocale: false });
};

/**
 * Handles 'suggestions' event - AI suggestions for user
 */
const handleSuggestionsEvent = (
  msg: { data: string },
  setSuggestions: React.Dispatch<React.SetStateAction<Suggestions | undefined>>,
) => {
  const suggestionsData: Suggestions = JSON.parse(
    msg.data,
  ) as Suggestions;

  setSuggestions(suggestionsData);
};

/**
 * Handles job search structured content from tool events
 */
const handleJobSearchToolContent = (
  toolData: {
    content: string;
    structured_content: {
      type: string;
      items: { data: ApiSchema<'JobItem'> }[];
      filters?: JobSearchFilters;
      search_keyword?: string;
    };
  },
  setCurrent: React.Dispatch<React.SetStateAction<StreamState | null>>,
) => {
  setCurrent((prevCurrent) => {
    const existingMessages = prevCurrent?.messages || [];

    const newMessage: Message = {
      id: `tool-${Date.now()}`,
      type: 'ai',
      content: toolData.content || '',
      response_metadata: {
        customData: {
          jobSearchResults: {
            items: Array.isArray(toolData.structured_content.items) ? toolData.structured_content.items
              .filter((item) => item?.data?.id)
              .map((item) => ({
                id: Number(item.data.id),
                score: 0,
                data: item.data,
              })) : [],
            cursor: '',
            type: 'job_search',
            filters: toolData.structured_content.filters,
            search_keyword: toolData.structured_content.search_keyword,
          },
          jobSearchRequest: (toolData.structured_content.filters as Record<string, unknown>) || undefined,
        },
      },
    };

    return {
      status: 'inflight',
      messages: [...existingMessages, newMessage],
      run_id: prevCurrent?.run_id,
    };
  });
};

/**
 * Handles job recommendations structured content from tool events
 */
const handleJobRecommendationsToolContent = (
  toolData: {
    content: string;
    structured_content: {
      type: string;
      items: RecommendationItem[];
    };
  },
  setCurrent: React.Dispatch<React.SetStateAction<StreamState | null>>,
) => {
  setCurrent((prevCurrent) => {
    const existingMessages = prevCurrent?.messages || [];

    const newMessage: Message = {
      id: `tool-${Date.now()}`,
      type: 'ai',
      content: toolData.content || '',
      response_metadata: {
        customData: {
          jobRecommendationResults: {
            items: Array.isArray(toolData.structured_content.items)
              ? toolData.structured_content.items
              : [],
          },
        },
      },
    };

    return {
      status: 'inflight',
      messages: [...existingMessages, newMessage],
      run_id: prevCurrent?.run_id,
    };
  });
};

/**
 * Handles resume search structured content from tool events
 */
const handleResumeSearchToolContent = (
  content: string,
  resumeSearchResult: { items: ApiSchema<'ResumeCardData'>[]; pages: number },
  resumeSearchRequest: { keyword?: string; location?: string[] } | undefined,
  setCurrent: React.Dispatch<React.SetStateAction<StreamState | null>>,
) => {
  setCurrent((prevCurrent) => {
    const existingMessages = prevCurrent?.messages || [];

    const newMessage: Message = {
      id: `tool-${Date.now()}`,
      type: 'ai',
      content: content || '',
      response_metadata: {
        customData: {
          resumeSearchResults: {
            items: resumeSearchResult.items,
            pages: resumeSearchResult.pages,
          },
          resumeSearchRequest,
        },
      },
    };

    return {
      status: 'inflight',
      messages: [...existingMessages, newMessage],
      run_id: prevCurrent?.run_id,
    };
  });
};

/**
 * Handles 'tool' event - structured content like job search results
 */
const handleToolEvent = (
  msg: { data: string },
  setCurrent: React.Dispatch<React.SetStateAction<StreamState | null>>,
  setFilters: React.Dispatch<React.SetStateAction<Filters | null>>,
) => {
  const toolData = JSON.parse(msg.data) as {
    content: string;
    structured_content?: {
      type: string;
      items: { data: unknown }[];
    };
  };

  if (toolData.structured_content?.type === 'resume_search') {
    const resumeData = toolData.structured_content as {
      type: string;
      resume_search_request?: { keyword?: string; location?: string[] };
      resume_search_result?: { items: ApiSchema<'ResumeCardData'>[]; pages: number };
    };
    const keyword = resumeData.resume_search_request?.keyword;
    if (keyword) {
      handleResumeSearchFilterEvent({ data: keyword }, setFilters);
    }
    if (resumeData.resume_search_result?.items) {
      handleResumeSearchToolContent(toolData.content, resumeData.resume_search_result, resumeData.resume_search_request, setCurrent);
    }
  } else if (toolData.structured_content?.type === 'job_search') {
    handleJobSearchToolContent(
      toolData as {
        content: string;
        structured_content: {
          type: string;
          items: { data: ApiSchema<'JobItem'> }[];
          filters?: JobSearchFilters;
          search_keyword?: string;
        };
      },
      setCurrent,
    );
  } else if (toolData.structured_content?.type === 'job_recommendations') {
    handleJobRecommendationsToolContent(
      toolData as unknown as {
        content: string;
        structured_content: {
          type: string;
          items: RecommendationItem[];
        };
      },
      setCurrent,
    );
  }
};

/**
 * Handles 'error' event - sets error status
 */
const handleErrorEvent = (
  setCurrent: React.Dispatch<React.SetStateAction<StreamState | null>>,
) => {
  setCurrent((prevCurrent) => ({
    status: 'error',
    messages: prevCurrent?.messages,
    run_id: prevCurrent?.run_id,
  }));
};

export interface UseStreamStateV2Options {
  /**
   * When provided, this thread_id is used INSTEAD of the anon session id. Used by the
   * resume-interview chat, which must run on the backend-provided thread_id so answers
   * persist against the backend `conversation_session`. When omitted, the generic Bestie
   * chat behaviour is unchanged (anon session id).
   */
  threadId?: string | null;
}

export function useStreamStateV2(
  context: string | undefined | null,
  options?: UseStreamStateV2Options,
): StreamStateProps {
  const [current, setCurrent] = useState<StreamState | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestions | undefined>(undefined);
  const [controller, setController] = useState<AbortController | null>(null);
  const anonSessionId = useAnonSessionId();
  // Injected thread_id (resume interview) takes precedence; otherwise fall back to the
  // anon session id so generic Bestie chat keeps its existing behaviour.
  const threadId = options?.threadId ?? anonSessionId;
  const [filters, setFilters] = useState<Filters | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);

  const { router, locale } = useExtendedRouter();


  const resetSearchFilters = () => {
    setFilters(null);
  };

  const fetchHistory = useCallback(async (callback?: () => void | Promise<void>): Promise<MessageHistoryResponse> => {
    if (!threadId) {
      console.warn('No threadId yet, skipping history fetch');
      return { messages: [], created_at: '', fetched: false };
    }
    try {
      setIsLoadingHistory(true);
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 10000); // 10 second timeout

      const historyUrl = `/api/agent/history/${threadId}`;

      const fetchResponse = await fetch(historyUrl, {
        method: 'GET',
        credentials: 'include',
        signal: abortController.signal,
      });

      clearTimeout(timeoutId);

      // A thread with no checkpoint yet answers 404 ("Thread <id> not found") — that is an empty
      // conversation, not a failure. Every first-time visitor hits it, so treat it as fetched and
      // empty; anything else is a real error and still throws.
      if (fetchResponse.status === 404) {
        return { messages: [], created_at: '', fetched: true };
      }

      if (!fetchResponse.ok) {
        throw new Error(`Failed to fetch history: ${fetchResponse.status}`);
      }

      const response: ConversationHistoryResponse = await fetchResponse.json() as ConversationHistoryResponse;

      // Strip past greeting exchanges (priming turn + reply) before this joins the live
      // messages — see `stripGreetingExchange` for why. Without it, every past visit's opener
      // would resurface here, including a duplicate of the current entry's own greeting.
      const historyMessages: Message[] = stripGreetingExchange(mapChatMessagesToMessages(response.messages));

      setCurrent((prevCurrent) => ({
        status: prevCurrent?.status || 'done',
        // History first: it is the older half, and on the chat page it is restored on demand
        // *after* Bestie's opener, so appending it would read out of order. This does not
        // de-duplicate anything between the two halves: history ids are synthetic (`history-N`,
        // assigned by `mapChatMessagesToMessages`) and live ids are `uuidv4()` / `'streaming'` /
        // `ai-<timestamp>` — the two id spaces are disjoint by construction, so
        // `mergeMessagesById` always appends every history message rather than merging it into
        // an existing live one.
        messages: mergeMessagesById(historyMessages, prevCurrent?.messages),
        run_id: prevCurrent?.run_id,
      }));

      if (callback) {
        await callback();
      }

      return {
        messages: historyMessages,
        created_at: response.last_message_at ?? '',
        fetched: true,
      };
    } catch (error) {
      console.error('Error fetching history', error);
      throw error;
    } finally {
      setIsLoadingHistory(false);
    }
  }, [threadId]);

  const startStream = useCallback(
    async (
      input: Message,
      name: string,
      config?: StartStreamConfig,
    ) => {
      const abortController = new AbortController();
      setController(abortController);

      const inputMessages: Message[] = [];
      if (input) {
        if (Array.isArray(input)) {
          const sanitized = input.map((msg: Message) => ({
            ...msg,
            content: sanitizeHtml(msg?.content),
          }));
          inputMessages.push(...sanitized);
        } else {
          const sanitized = {
            ...input,
            content: sanitizeHtml(input.content),
          };
          inputMessages.push(sanitized);
        }
      }

      setCurrent((prevCurrent) => ({
        status: 'inflight',
        messages: [...(prevCurrent?.messages || []), ...inputMessages],
        run_id: prevCurrent?.run_id,
      }));

      // TODO(agents): chat/stream must accept prompt + extraction_schema and run SSE
      // extraction on completion — not yet implemented agent-side. The frontend forwards
      // them when present (resume-interview chat); the generic Bestie body is unchanged.
      const body: Record<string, unknown> = {
        message: input.content,
        thread_id: threadId,
        images: null,
        ...(config?.prompt && { prompt: config.prompt }),
        ...(config?.extractionSchema !== undefined && { extraction_schema: config.extractionSchema }),
      };

      await fetchEventSource(`/api/agent/chat/stream${context ? `?context=${context}` : ''}`, {
        signal: abortController.signal,
        method: 'POST',
        headers: {
          ...(config?.headers ?? {}),
          'content-type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body),
        openWhenHidden: true,
        onopen: async (response) => {
          if (
            response.ok
            && response.headers.get('content-type') === 'text/event-stream'
          ) {
            // Connection established
          } else if (response.status === 402) {
            throw new Error('Subscription required');
          } else if (
            response.status >= 400
            && response.status < 500
            && response.status !== 429
          ) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        },
        onmessage(msg) {
          if (msg.event === 'text') {
            handleTextEvent(msg, setCurrent);
          } else if (msg.event === 'data') {
            handleDataEvent(msg, setCurrent);
          } else if (msg.event === 'resume_search_filter') {
            handleResumeSearchFilterEvent(msg, setFilters);
          } else if (msg.event === 'metadata') {
            handleMetadataEvent(msg, setCurrent);
          } else if (msg.event === 'job_search_intent') {
            handleJobSearchIntentEvent(msg, locale, router);
          } else if (msg.event === 'suggestions') {
            handleSuggestionsEvent(msg, setSuggestions);
          } else if (msg.event === 'tool') {
            handleToolEvent(msg, setCurrent, setFilters);
          } else if (msg.event === 'error') {
            handleErrorEvent(setCurrent);
          }
        },
        onclose() {
          setCurrent((prevCurrent) => {
            const messages = prevCurrent?.messages?.map((msg) => (msg.id === 'streaming'
              ? { ...msg, id: `ai-${Date.now()}` }
              : msg));
            return {
              status: prevCurrent?.status === 'error' ? prevCurrent.status : 'done',
              messages,
              run_id: prevCurrent?.run_id,
            };
          });
          setController(null);
        },
        onerror(error) {
          setCurrent((prevCurrent) => ({
            status: 'error',
            messages: prevCurrent?.messages,
            run_id: prevCurrent?.run_id,
          }));
          setController(null);
          throw error;
        },
      });
    },
    [threadId, locale, router, context],
  );

  const stopStream = useCallback(
    (clear: boolean = false) => {
      controller?.abort();
      setController(null);
      if (clear) {
        setCurrent((prevCurrent) => ({
          status: 'done',
          run_id: prevCurrent?.run_id,
        }));
      } else {
        setCurrent((prevCurrent) => {
          const messages = prevCurrent?.messages?.map((msg) => (msg.id === 'streaming'
            ? { ...msg, id: `ai-${Date.now()}` }
            : msg));
          return {
            status: 'done',
            messages,
            run_id: prevCurrent?.run_id,
          };
        });
      }
    },
    [controller],
  );

  const clearBiaSuggestions = useCallback(
    () => {
      setSuggestions(undefined);
    },
    [],
  );

  // Seed a pre-built message into the stream without hitting the network — used to show the
  // agent's opening line returned by chat/invoke as the first message (resume interview),
  // instead of an extra history round-trip. No-op if a message with the same id already exists.
  const seedMessage = useCallback((message: Message) => {
    setCurrent((prevCurrent) => {
      const existing = prevCurrent?.messages ?? [];
      if (existing.some((m) => m.id === message.id)) {
        return prevCurrent;
      }
      return {
        status: prevCurrent?.status ?? 'done',
        messages: [...existing, message],
        run_id: prevCurrent?.run_id,
      };
    });
  }, []);

  return {
    startStream,
    stopStream,
    stream: current,
    initializeWithHistory: fetchHistory, // Include the new method
    isLoadingHistory,
    biaSuggestions: suggestions,
    filters,
    resetSearchFilters,
    clearBiaSuggestions,
    seedMessage,
  };
}
